/**
 * Pumpkin carving stencils — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pumpkinCarvingStencilsPack: ToilePack = {
    slug: 'pumpkin-carving-stencils',
    category: 'season',
    name: 'Pumpkin carving stencils',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'face', label: 'Classic face', subject: 'A classic grinning jack-o-lantern face'},
        {key: 'cat', label: 'Cat', subject: 'An arched black cat silhouette'},
        {key: 'house', label: 'Haunted house', subject: 'A haunted house with lit windows'},
    ],
    styles: [
        {key: 'single', label: 'Single-layer', style: 'a bold single-layer stencil, large solid cut-shapes with wide bridges', palette: 'Solid black on white', framing: 'Easy to cut, centered with a wide white margin'},
        {key: 'detailed', label: 'Detailed', style: 'a multi-detail layered stencil, finer solid cut-shapes with narrow bridges', palette: 'Solid black on white', framing: 'More intricate, centered with a moderate white margin'},
        {key: 'mini', label: 'Mini', style: 'a small mini stencil, compact solid shapes with delicate bridges', palette: 'Solid black on white', framing: 'For mini pumpkins and gourds, centered with a wide white margin'},
    ],
    directions: ['a spider instead', 'for a mini pumpkin', 'as a lettered word'],
    directionHint: 'try: a spider instead',
    seo: {
        title: 'Pumpkin Carving Stencils: Classic Face, Cat, Haunted House',
        metaDescription: "Nine pumpkin carving stencils — a classic jack-o'-lantern face, a cat, a haunted house — solid cut areas with bridges, three styles.",
        h1: 'Pumpkin carving stencils: classic face, cat, haunted house',
        tagline: 'Nine stencils, three October subjects, three styles each.',
        intro: "Templates taped to the pumpkin: a classic jack-o'-lantern face, a cat, a haunted house, each marked with solid black where you cut and bridges so the carved piece holds together. Ask for a bat, a spider, or your own subject, and the same three styles carry it.",
        useCases: [
            'a family pumpkin-carving night',
            'a school or community carving contest',
            'a porch display design',
            "a shop or event's carving-station handout",
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine carving templates — a classic face, a cat, a haunted house — with solid black cut areas and holding bridges, in three styles.'},
            {q: 'How do I actually transfer this onto a pumpkin?', a: 'Print the template, tape it to the pumpkin, poke through the outlines with a pin or toothpick, then carve the marked solid areas along the poked lines.'},
            {q: 'What are the bridges in the design for?', a: "Bridges are the small connecting strips left between cut-out sections so the carved piece holds its shape once it's hollowed out."},
            {q: 'Can I get a different subject, like a bat or spider?', a: 'Yes — the direction field takes any Halloween subject, including a bat or a spider.'},
            {q: 'Is carving safe for kids to do with this template?', a: 'The template only marks where to cut — adult supervision with sharp tools is still the safe approach for any pumpkin carving.'},
        ],
    },
}
